const BODY_READ_FAILED = {
  bodyReadFailed: true
};
export function readBody(res, responseType) {
  return res.text().then(function (text) {
    if (responseType === 'text') return text;
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }, function () {
    return BODY_READ_FAILED;
  });
}
export function networkFailure() {
  return Promise.reject({
    status: 0,
    data: null
  });
}
function applyOverrides(headers, overrides) {
  if (!overrides) return headers;
  for (const name of Object.keys(overrides)) {
    const lower = name.toLowerCase();
    for (const existing of Object.keys(headers)) {
      if (existing !== name && existing.toLowerCase() === lower) delete headers[existing];
    }
    headers[name] = overrides[name];
  }
  return headers;
}
export function requestHeaders(headerProvider, hasBody, base, overrides) {
  if (!headerProvider) return Promise.resolve(applyOverrides(base, overrides));
  return Promise.resolve().then(function () {
    return headerProvider.headers(hasBody);
  }).then(function (provided) {
    return applyOverrides(Object.assign(base, provided || {}), overrides);
  });
}
export function adoptResponseHeaders(headerProvider, res) {
  if (!headerProvider || !headerProvider.onResponseHeaders) return Promise.resolve();
  return Promise.resolve().then(function () {
    return headerProvider.onResponseHeaders(function (name) {
      try {
        return res.headers ? res.headers.get(name) : null;
      } catch (e) {
        return null;
      }
    });
  }).catch(function () {});
}
export function responseHeaderGetter(res) {
  if (!res || !res.headers || typeof res.headers.get !== 'function') return null;
  return function (name) {
    if (!name) return null;
    try {
      const value = res.headers.get(name);
      return value === undefined ? null : value;
    } catch (e) {
      return null;
    }
  };
}
export function normalizeResponse(headerProvider, res, exposeHeaders, responseType) {
  return readBody(res, responseType).then(function (data) {
    const ok = res.status >= 200 && res.status < 300;
    if (data === BODY_READ_FAILED && ok) return networkFailure();
    const body = data === BODY_READ_FAILED ? null : data;
    return adoptResponseHeaders(headerProvider, res).then(function () {
      const out = {
        status: res.status,
        data: body
      };
      const getHeader = exposeHeaders ? responseHeaderGetter(res) : null;
      if (getHeader) out.headers = getHeader;
      if (ok) return out;
      if (typeof res.statusText === 'string') out.statusText = res.statusText;
      return Promise.reject(out);
    });
  });
}
export function keepContractRejection(err) {
  if (err && typeof err.status === 'number') return Promise.reject(err);
  return networkFailure();
}
export function abortSignalFor(cancel, timeoutMs) {
  const timed = typeof timeoutMs === 'number' && timeoutMs > 0 && Number.isFinite(timeoutMs);
  if (!cancel && !timed || typeof AbortController !== 'function') return null;
  const controller = new AbortController();
  let timer = null;
  if (cancel) {
    Promise.resolve(cancel).then(function () {
      controller.abort();
    }, function () {});
  }
  if (timed) timer = setTimeout(function () {
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    settle: function () {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    }
  };
}
export function untilAbort(promise, abort) {
  if (!abort) return promise;
  if (abort.signal.aborted) return Promise.reject({
    status: 0,
    data: null
  });
  return new Promise(function (resolve, reject) {
    const onAbort = function () {
      reject({
        status: 0,
        data: null
      });
    };
    abort.signal.addEventListener('abort', onAbort);
    Promise.resolve(promise).then(function (value) {
      abort.signal.removeEventListener('abort', onAbort);
      resolve(value);
    }, function (err) {
      abort.signal.removeEventListener('abort', onAbort);
      reject(err);
    });
  });
}
export function settleWith(promise, abort) {
  if (!abort) return promise;
  return promise.then(function (value) {
    abort.settle();
    return value;
  }, function (err) {
    abort.settle();
    return Promise.reject(err);
  });
}